import type { TranslationPlaceholder } from '#layers/i18n/types';
import type { SafeKeyOf, SafePick, TranslatableRecord, TranslateObjectOptions } from '#layers/i18n/utils/translate-object';
import { getCurrentInstance } from 'vue';
import { useI18n } from 'vue-i18n';
import { useNuxtApp } from '#app/nuxt';
import { translateObject } from '#layers/i18n/utils/translate-object';
import { translateString } from '#layers/i18n/utils/translate-string';

type BoundOptions<K, P extends boolean> = Omit<TranslateObjectOptions<K, P>, 'i18n'>;

interface UseTranslationUtilsReturn {
	translateString: <S extends TranslationPlaceholder | undefined>(s: S) => S;
	translateObject: {
		<T extends TranslatableRecord, K extends SafeKeyOf<T>>(object: T, options?: BoundOptions<K, false>): T;
		<T extends TranslatableRecord, K extends SafeKeyOf<T>>(object: T, options: BoundOptions<K, true>): SafePick<T, K>;
	};
}

export function useTranslationUtils(): UseTranslationUtilsReturn {
	// Shared singletons (`createSharedComposable`) initialize lazily on their first
	// call, which can happen from a `computed` or a plugin-provided callback rather
	// than from a `setup()`. `useI18n()` throws there for lack of a component
	// instance, so fall back to the app-level composer, which resolves the same keys.
	const i18n = getCurrentInstance() ? useI18n() : useNuxtApp().$i18n;

	return {
		translateString: (s) => translateString(s, i18n),
		translateObject: ((object: any, options?: any) => translateObject(object, { ...options, i18n })) as UseTranslationUtilsReturn['translateObject'],
	};
}
